import { prettyTime } from "@/lib/format";

const usd = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const Panel = ({ testId, title, children }) => (
  <div className="mt-12 border border-bone/10 bg-forest/30 p-8" data-testid={testId}>
    <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">{title}</p>
    {children}
  </div>
);

const Table = ({ headers, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-sm text-bone/80">
      <thead>
        <tr className="border-b border-bone/10 font-mono text-[9px] uppercase tracking-[0.2em] text-bone/40">
          {headers.map((h, i) => (
            <th key={h} className={i === headers.length - 1 ? "py-3" : "py-3 pr-6"}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const UpcomingBookingsTable = ({ bookings }) => (
  <Panel testId="upcoming-bookings-table" title="Upcoming consultations">
    {bookings.length === 0 ? (
      <p className="text-sm text-bone/50">No upcoming bookings.</p>
    ) : (
      <Table headers={["When (PT)", "Name", "Email", "Service"]}>
        {bookings.map((b, i) => (
          <tr key={i} className="border-b border-bone/5" data-testid={`upcoming-booking-${i}`}>
            <td className="py-3 pr-6 font-mono text-xs text-terra">{b.date} · {prettyTime(b.time)}</td>
            <td className="py-3 pr-6">{b.name}</td>
            <td className="py-3 pr-6">{b.email}</td>
            <td className="py-3">{[b.service, b.tier].filter(Boolean).join(" · ") || "—"}</td>
          </tr>
        ))}
      </Table>
    )}
  </Panel>
);

export const LeadsTable = ({ leads }) => (
  <Panel testId="checklist-leads-table" title="Recent checklist leads">
    {(!leads || leads.length === 0) ? (
      <p className="text-sm text-bone/50">No leads captured yet.</p>
    ) : (
      <Table headers={["Captured", "Name", "Email", "Checklist"]}>
        {leads.map((l, i) => (
          <tr key={i} className="border-b border-bone/5" data-testid={`checklist-lead-${i}`}>
            <td className="py-3 pr-6 font-mono text-xs text-terra">{l.created_at?.slice(0, 10)}</td>
            <td className="py-3 pr-6">{l.name || "—"}</td>
            <td className="py-3 pr-6">{l.email}</td>
            <td className="py-3 capitalize">{l.type}</td>
          </tr>
        ))}
      </Table>
    )}
  </Panel>
);

export const PaymentsTable = ({ payments }) => (
  <Panel testId="payments-table" title="Recent payments">
    {(!payments || payments.length === 0) ? (
      <p className="text-sm text-bone/50">No online payments yet — this fills in as clients pay from the pricing section.</p>
    ) : (
      <Table headers={["Paid", "Client", "Service", "Via", "Amount"]}>
        {payments.map((p, i) => (
          <tr key={i} className="border-b border-bone/5" data-testid={`payment-row-${i}`}>
            <td className="py-3 pr-6 font-mono text-xs text-terra">{p.paid_at?.slice(0, 10)}</td>
            <td className="py-3 pr-6">{[p.name, p.email].filter(Boolean).join(" · ") || "—"}</td>
            <td className="py-3 pr-6">{p.label}</td>
            <td className="py-3 pr-6 font-mono text-[10px] uppercase tracking-[0.15em]">{p.provider || "paypal"}</td>
            <td className="py-3 font-mono text-xs">{usd(p.amount)}</td>
          </tr>
        ))}
      </Table>
    )}
  </Panel>
);

export const InstalmentPlansTable = ({ plans }) => (
  <Panel testId="instalment-plans-table" title="Instalment plans">
    {(!plans || plans.length === 0) ? (
      <p className="text-sm text-bone/50">No instalment plans yet — this fills in when a client splits a package into monthly payments.</p>
    ) : (
      <Table headers={["Started", "Client", "Service", "Progress", "Type", "Status"]}>
        {plans.map((p, i) => (
          <tr key={i} className="border-b border-bone/5" data-testid={`instalment-row-${i}`}>
            <td className="py-3 pr-6 font-mono text-xs text-terra">{p.created_at?.slice(0, 10)}</td>
            <td className="py-3 pr-6">{[p.name, p.email].filter(Boolean).join(" · ") || "—"}</td>
            <td className="py-3 pr-6">{p.label}</td>
            <td className="py-3 pr-6 font-mono text-xs">{p.paid_count}/{p.cycles} × {usd(p.instalment_amount)}</td>
            <td className="py-3 pr-6 font-mono text-[10px] uppercase tracking-[0.15em]">
              {p.collection === "auto" ? "Auto-pay" : "Email link"}
            </td>
            <td className="py-3 font-mono text-[10px] uppercase tracking-[0.15em]">
              {p.status === "active" && p.next_due ? `Next due ${p.next_due}` : p.status}
            </td>
          </tr>
        ))}
      </Table>
    )}
  </Panel>
);

export const ReferralsTable = ({ referrals, totalCodes }) => (
  <Panel testId="referrals-table" title={`Referral rewards ${totalCodes ? `(${totalCodes} links created)` : ""}`}>
    {(!referrals || referrals.length === 0) ? (
      <p className="text-sm text-bone/50">No rewards earned yet — this fills in when a referred friend books or pays. Each reward is a $50 credit to apply manually on the referrer's next invoice.</p>
    ) : (
      <Table headers={["Referrer", "Code", "Friends converted", "Credit owed", "Last activity"]}>
        {referrals.map((r, i) => (
          <tr key={i} className="border-b border-bone/5" data-testid={`referral-row-${i}`}>
            <td className="py-3 pr-6">{[r.name, r.email].filter(Boolean).join(" · ")}</td>
            <td className="py-3 pr-6 font-mono text-xs text-terra">{r.code}</td>
            <td className="py-3 pr-6 font-mono text-xs">{r.friends}</td>
            <td className="py-3 pr-6 font-mono text-xs">{usd(r.reward_total)}</td>
            <td className="py-3 font-mono text-xs">{r.last_event?.slice(0, 10)}</td>
          </tr>
        ))}
      </Table>
    )}
  </Panel>
);
